import { PolfEventModeEnum } from '@polf/shared/codegen/types'
import { formatChips } from '@polf/shared/locale'
import {
  FOURSOME_ALLOWANCE,
  getCourseHandicap,
  getCourseStrokes,
  SCRAMBLE_WEIGHTS,
} from '@polf/shared/scoring'
import { type ReactNode, useMemo } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'

import {
  type ProfilePlayerFragment,
  useCurrentPlayerFlightScoresQuery,
  useCurrentPlayerProfileQuery,
} from '../__generated__/hooks'
import { IconCard, IconLeaderboard, IconTarget } from '../components/Icon'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import type { CurrentPlayerCtx, Phase } from '../PlayerShell'
import { isOneBallTeamMode, usesChips } from '../util/event-mode'
import { initialsOf } from '../util/initials'
import { holeStrokesFor } from '../util/strokes'

/**
 * Player profile — hero card, strokes-per-hole map, round info, chip rules.
 * Team-aware: Scramble shows team-only (team strokes, team stroke-map,
 * roster); BestBall shows player-level data + a team card listing members;
 * Golf / POLF show individual data (unchanged).
 *
 * Deferred (no API support):
 *   - QR code for poker-phase payouts
 *   - Avatar upload / photo
 */

const fmtTime = (input: string | null | undefined): string => {
  if (!input) return '—'
  // startTime is a MySQL `time` column — arrives as "HH:MM:SS", which
  // new Date() can't parse. Show HH:MM.
  const hhmmss = input.match(/^(\d{2}:\d{2})(?::\d{2})?$/)
  if (hhmmss) return hhmmss[1]
  const d = new Date(input)
  return Number.isNaN(d.getTime())
    ? input
    : d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })
}

/**
 * The handicap allowance as a percentage — `85 %`, the unit the organizer sets
 * it in and the one the scramble weightings already use. Keeps a decimal where
 * there is one rather than rounding: prod holds allowances like 0,666, and a
 * label claiming 67 % over a total computed from 66,6 % explains nothing.
 */
const formatAllowance = (allowance: number): string =>
  `${String(+(allowance * 100).toFixed(1)).replace('.', ',')} %`

const StatusPill = ({
  submitted,
  approved,
}: {
  submitted: boolean
  approved: boolean
}) => {
  if (approved) {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-700 text-paper">
        Godkjent
      </span>
    )
  }
  if (submitted) {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-100 text-green-800">
        Levert
      </span>
    )
  }
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cream-card text-ink-500">
      Pågår
    </span>
  )
}

/**
 * Hero card. Subtitle adapts to the team format:
 *  - One ball per team (Scramble, Foursome): team name + team strokes (the
 *    player's own hcp is irrelevant — they play the team's allowance)
 *  - everything else: player hcp + individual strokes
 */
const Hero = ({
  profile,
  isOneBall,
}: {
  profile: ProfilePlayerFragment
  isOneBall: boolean
}) => {
  const { team } = profile
  const displayName = isOneBall && team?.name ? team.name : profile.name
  const subtitle = isOneBall ? (
    <>
      <span className="font-mono num">{team?.strokes ?? '—'}</span> tildelte
      slag
    </>
  ) : (
    <>
      HCP <span className="font-mono num">{profile.hcp ?? '—'}</span> ·{' '}
      <span className="font-mono num">{profile.strokes}</span> tildelte slag
    </>
  )
  return (
    <div className="mx-2 my-3 bg-green-900 text-paper rounded-lg p-5 flex flex-col gap-3 shadow">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-paper text-green-900 flex items-center justify-center text-lg font-bold shrink-0">
          {initialsOf(displayName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-xl truncate">
            {displayName}
          </div>
          <div className="text-sm text-green-100">{subtitle}</div>
        </div>
        <StatusPill
          submitted={profile.scoresSubmitted}
          approved={profile.scoresApproved}
        />
      </div>
      {/* No flight number here — players read "Flight 3" as their start hole.
          The number carries no player-facing meaning; start hole + time do. */}
      <div className="grid grid-cols-3 gap-3 text-center border-t border-green-800 pt-3">
        <div>
          <div className="text-[10px] up text-green-100">Starthull</div>
          <div className="font-display font-bold text-lg">
            {profile.flight?.startHole?.number ?? '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] up text-green-100">Starttid</div>
          <div className="font-display font-bold text-lg num">
            {profile.flight?.startTime
              ? fmtTime(profile.flight.startTime)
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] up text-green-100">Utslagssted</div>
          <div className="font-display font-bold text-lg">
            {profile.courseTee?.teeName ?? '—'}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Team roster card. Shown on every team event, and the formats want different
 * numbers because they reach a team handicap by different routes.
 *
 *  - Scramble: each member's COURSE handicap and its weighting, then the sum
 *    row carrying the team's canonical (server-computed) total. Allowance-free,
 *    because the weighting is itself the allowance — matching
 *    `getScrambleTeamStrokes`, which orders by course handicap so a mixed-tee
 *    roster gets the top weight on whoever receives fewest strokes.
 *  - Foursome: both partners' COURSE handicaps and their sum, halved — WHS
 *    Appendix C's 50 % applies to the pair, not to either player, so there is
 *    no per-row badge to show.
 *  - BestBall: each member's ALLOTTED STROKES. There is no team allowance;
 *    everyone plays their own ball off their own, so a course handicap here is
 *    a number nobody plays off — and on an event below full allowance it
 *    contradicts the breakdown card directly below (POL-76).
 */
/**
 * A fixed-width numeric cell. Widths are what make these rows a table: a
 * right-aligned flex group alone lets every cell slide left when its
 * neighbour grows, so a 7 and a 28 in the same column don't share an edge
 * and the tee beside them drifts row to row.
 */
const NumCell = ({
  children,
  className = '',
  width,
}: {
  children: ReactNode
  className?: string
  width: string
}) => (
  <span className={`${width} text-right shrink-0 ${className}`}>
    {children}
  </span>
)

/**
 * The tee a row plays from, as its own column ahead of the numbers it
 * explains. A course handicap is an index adjusted for the tee, so on mixed
 * tees the number beside a name looks unexplained without it — and the hero
 * only ever shows the reader's own. Fixed width so the numbers below it line
 * up as a column rather than drifting with the tee's name length.
 */
const TeeColumn = ({ teeName }: { teeName?: string | null }) => (
  <NumCell width="w-7" className="text-[11px] text-ink-500 truncate">
    {teeName ?? '—'}
  </NumCell>
)

/** WHS Appendix C's foursome share, on whichever row it produced. */
const AllowanceBadge = () => (
  <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
    {Math.round(FOURSOME_ALLOWANCE * 100)} %
  </span>
)

const TeamCard = ({
  team,
  mode,
  hcpFactor,
}: {
  team: NonNullable<ProfilePlayerFragment['team']>
  mode: PolfEventModeEnum
  hcpFactor: number
}) => {
  const isScramble = mode === PolfEventModeEnum.Scramble
  const isFoursome = mode === PolfEventModeEnum.Foursome
  // Both one-ball formats list course handicaps: it is the number the team
  // allowance is computed from, and the only one a partner can check.
  const isOneBall = isScramble || isFoursome
  const allPlayers = team.players ?? []
  // Both numbers derived here rather than selected: PolfEventPlayer.strokes
  // needs joins the team's players relation doesn't load. Same shared helpers
  // the server uses, on the same inputs. Missing an hcp or a tee drops a member
  // to the bottom; they wouldn't be valid for a Scramble strokes calc anyway.
  const withCourseHcp = allPlayers.map((p) => ({
    ...p,
    courseHcp:
      p.hcp != null && p.courseTee
        ? getCourseHandicap(p.hcp, p.courseTee)
        : null,
    strokes:
      p.hcp != null && p.courseTee
        ? getCourseStrokes(p.hcp, p.courseTee, hcpFactor)
        : null,
  }))
  const sortValue = (p: (typeof withCourseHcp)[number]) =>
    (isOneBall ? p.courseHcp : p.strokes) ?? Number.POSITIVE_INFINITY
  const sortedPlayers = [...withCourseHcp].sort(
    (a, b) => sortValue(a) - sortValue(b)
  )
  const pctList: number[] | undefined = isScramble
    ? SCRAMBLE_WEIGHTS[sortedPlayers.length]
    : undefined

  // What the allowance produced before rounding — a weighted sum for scramble,
  // the halved combined pair for a foursome.
  const unrounded = sortedPlayers.reduce((acc, p, i) => {
    const pct = isFoursome ? FOURSOME_ALLOWANCE : pctList?.[i]
    return acc + (pct != null && p.courseHcp != null ? p.courseHcp * pct : 0)
  }, 0)
  // Show it only when it says something the total doesn't. A foursome pair
  // halves to a whole number as often as not, and a row reading 16,0 above a
  // row reading 16 is two lines for one fact (design.md §6.3b). When there IS
  // a half stroke, the row is the whole point: it shows where the rounding
  // happened. Scramble's weighted sum is a genuine intermediate step, so it
  // always shows.
  const showUnrounded = !isFoursome || Math.round(unrounded) !== unrounded

  return (
    <div className="mx-2 my-3 bg-paper rounded-lg p-4 shadow flex flex-col gap-2">
      <div className="up text-ink-500">Laget</div>
      <div className="font-display font-bold text-lg text-ink-900 truncate">
        {team.name ?? 'Uten navn'}
      </div>
      {isScramble ? (
        <p className="text-[11px] text-ink-500">
          Banehandicap fra utslagsstedet hver spiller bruker — lavest får størst
          andel av lagets handicap.
        </p>
      ) : null}
      {isFoursome ? (
        <p className="text-[11px] text-ink-500">
          Banehandicap fra utslagsstedet hver spiller bruker — laget spiller på
          halvparten av summen.
        </p>
      ) : null}
      <ul className="flex flex-col mt-1" aria-label="Laget">
        {sortedPlayers.map((p, i) => {
          const pct = pctList?.[i]
          const contribution =
            pct != null && p.courseHcp != null ? p.courseHcp * pct : null
          return (
            <li
              key={p.id}
              className="flex items-center justify-between text-sm py-1 border-t border-ink-100 first:border-t-0"
            >
              <span className="font-semibold text-ink-900 truncate">
                {p.name}
              </span>
              {isOneBall ? (
                <span className="flex items-center gap-2 shrink-0 font-mono num">
                  <TeeColumn teeName={p.courseTee?.teeName} />
                  <NumCell width="w-7" className="text-ink-500">
                    {p.courseHcp ?? '—'}
                  </NumCell>
                  {pct != null ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">
                      {Math.round(pct * 100)} %
                    </span>
                  ) : null}
                  {contribution != null ? (
                    <NumCell width="w-12" className="text-ink-700">
                      = {contribution.toFixed(1)}
                    </NumCell>
                  ) : null}
                </span>
              ) : (
                // Same shape as the flight card: the index muted, the number
                // they actually play off carrying the weight.
                <span className="flex items-center gap-3 shrink-0 font-mono num">
                  <TeeColumn teeName={p.courseTee?.teeName} />
                  <NumCell width="w-16" className="text-ink-500">
                    {p.hcp != null ? `HCP ${p.hcp}` : ''}
                  </NumCell>
                  <NumCell width="w-20" className="font-semibold text-ink-900">
                    {p.strokes == null
                      ? '—'
                      : p.strokes > 0
                      ? `${p.strokes} slag`
                      : 'ingen slag'}
                  </NumCell>
                </span>
              )}
            </li>
          )
        })}
        {isOneBall && team.strokes != null ? (
          <>
            {showUnrounded ? (
              <li className="flex items-center justify-between text-sm py-1.5 border-t border-ink-100 text-ink-700">
                <span className="flex items-center gap-2">
                  {isFoursome ? 'Halve summen' : 'Vektet banehandicap'}
                  {isFoursome ? <AllowanceBadge /> : null}
                </span>
                <span className="font-mono num font-semibold">
                  {unrounded.toFixed(1)}
                </span>
              </li>
            ) : null}
            <li className="flex items-center justify-between text-sm py-2 border-t-2 border-ink-200 font-semibold">
              <span className="flex items-center gap-2 text-ink-900">
                Tildelte slag
                {isFoursome && !showUnrounded ? <AllowanceBadge /> : null}
              </span>
              <span className="font-mono num font-bold text-ink-900">
                {team.strokes}
              </span>
            </li>
            <li className="text-[11px] text-ink-500 pt-1">
              Banehandicap er spillerens hcp justert for slope og course rating
              på utslagsstedet de spiller fra.
            </li>
          </>
        ) : null}
      </ul>
    </div>
  )
}

/**
 * Where `N slag` comes from (design.md §6.3b). A player reads HCP in the topbar
 * and allotted strokes on the hero with nothing joining them, and one who checks
 * the app against GolfBox and gets a different number concludes the app is
 * wrong. WHS reaches the total in two rounded steps, so the card shows both.
 *
 * The last row is the SERVER's `strokes`, not a second opinion: the rows are
 * recomputed from the same inputs the API used, and if they fail to land on it
 * the breakdown is dropped rather than shown contradicting the leaderboard.
 */
const AllowanceCard = ({ profile }: { profile: ProfilePlayerFragment }) => {
  const { hcp, strokes, courseTee } = profile
  const allowance = profile.polfEvent.hcpFactor
  if (hcp == null || !courseTee) return null

  const courseHcp = getCourseHandicap(hcp, courseTee)
  const derived = getCourseStrokes(hcp, courseTee, allowance)
  if (derived !== strokes) return null

  return (
    <div className="mx-2 my-3 bg-paper rounded-lg p-4 shadow flex flex-col gap-2">
      <div className="up text-ink-500">Tildelte slag</div>
      <ul className="flex flex-col" aria-label="Tildelte slag">
        <li className="flex items-center justify-between text-sm py-1.5 border-t border-ink-100 first:border-t-0">
          <span className="text-ink-700">HCP</span>
          <span className="font-mono num text-ink-900">{hcp}</span>
        </li>
        <li className="flex items-center justify-between text-sm py-1.5 border-t border-ink-100">
          <span className="text-ink-700">Tildelte slag</span>
          <span className="font-mono num text-ink-900">{courseHcp}</span>
        </li>
        {/* One row per number: the allowance rides in the label of the row it
            produced rather than taking a line of its own to state a multiplier
            and then a second line to repeat its result. At a full allowance the
            course handicap IS the allotted strokes, so there is no third row. */}
        {allowance !== 1 ? (
          <li className="flex items-center justify-between text-sm py-2 border-t-2 border-ink-200 font-semibold">
            <span className="text-ink-900">
              Tildelte slag ({formatAllowance(allowance)})
            </span>
            <span className="font-mono num font-bold text-ink-900">
              {strokes}
            </span>
          </li>
        ) : null}
      </ul>
    </div>
  )
}

/**
 * The whole flight's allowances (POL-75). One person normally enters and reads
 * out scores for the group, so they need everyone's strokes, not only their own.
 *
 * Deliberately the player's OWN course-handicap allowance, not the match
 * differential: POL-79 settled that the match number belongs on the score card
 * (the hole in front of you) and the stroke-play number on the reading surfaces
 * where there is room to label it. This is one of those surfaces. On a match
 * event a footnote says so rather than showing a second column.
 *
 * Scramble entries arrive keyed by team, so the card lists teams — for that
 * mode the team IS the playing unit and a per-player allowance would be a
 * number nobody plays off.
 */
const FlightCard = ({
  playerId,
  isOneBall: oneBall,
  matchPlay,
}: {
  playerId: string
  isOneBall: boolean
  matchPlay: boolean
}) => {
  // cache-and-network, unlike the strokes map below: landing straight on the
  // profile (a bookmarked /profile, or the poker phase's Min runde) has no
  // flight scores cached yet, and this card is the reason to be here.
  const { data } = useCurrentPlayerFlightScoresQuery({
    fetchPolicy: 'cache-and-network',
  })
  // In a one-ball format the entries ARE the teams, which is what the heading
  // below says. Everywhere else this card reads out tee, handicap and allotted
  // strokes — all of them a person's — so a split round's team entries are not
  // rows here. Listing them made a pair appear twice, once as a player-shaped
  // row with a blank handicap claiming "ingen slag".
  const all = data?.currentPlayerFlightScores ?? []
  const entries = oneBall ? all : all.filter((e) => !e.isTeamEntry)
  // A flight of one has nobody to read strokes out to, and in a one-ball
  // format that lone entry is the team the card above already covers in full.
  if (entries.length < 2) return null

  const heading = oneBall ? 'Lagene i flighten' : 'Flighten'

  return (
    <div className="mx-2 my-3 bg-paper rounded-lg p-4 shadow flex flex-col gap-2">
      {/* No flight number, per the hero: players read "Flight 3" as a hole. */}
      <div className="up text-ink-500">{heading}</div>
      {/* Names the columns once instead of labelling every row. Without it the
          leading tee is a bare number sitting next to two other numbers. */}
      {oneBall ? null : (
        <p className="text-[11px] text-ink-500">
          Utslagssted · hcp · tildelte slag
        </p>
      )}
      <ul className="flex flex-col" aria-label={heading}>
        {entries.map((e) => {
          const isYou = e.id === playerId
          return (
            <li
              key={e.id}
              className={`flex items-center justify-between gap-2 text-sm py-2 border-t border-ink-100 first:border-t-0 ${
                isYou
                  ? 'bg-green-50 border-l-[3px] border-l-green-700 pl-2'
                  : ''
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="font-semibold text-ink-900 truncate">
                  {e.name}
                </span>
                {isYou ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-green-700 text-paper shrink-0">
                    Du
                  </span>
                ) : null}
              </span>
              <span className="flex items-center gap-2 shrink-0 font-mono num">
                {/* Null on a one-ball row, which is a team playing two tees —
                    the team card carries them per member instead. */}
                {oneBall ? null : <TeeColumn teeName={e.courseTee?.teeName} />}
                {/* No inline `HCP` label: the legend above names the columns,
                    and repeating it on every row costs the width that made
                    `ingen slag` wrap. */}
                <NumCell width="w-10" className="text-ink-500">
                  {e.hcp ?? ''}
                </NumCell>
                <NumCell width="w-20" className="font-semibold text-ink-900">
                  {(e.strokes ?? 0) > 0 ? `${e.strokes} slag` : 'ingen slag'}
                </NumCell>
              </span>
            </li>
          )
        })}
      </ul>
      {matchPlay ? (
        <p className="text-[11px] text-ink-500">
          I match play vil differansen mellom spillere være spillende handicap.
        </p>
      ) : null}
    </div>
  )
}

/**
 * Strokes-per-hole map. Each cell shows the hole number with up to N
 * red dots underneath representing strokes given.
 *
 *  - Scramble: team strokes (per-hole distribution of the team's total).
 *    The flight-scores entry for the player's team has `strokes` already
 *    resolved per hole via the score entity's getter.
 *  - BestBall / Golf / POLF: the player's individual strokes.
 *
 * Reads from currentPlayerFlightScores (already in Apollo cache from the
 * Score / Scorecard pages in most flows).
 */
const StrokesMap = ({
  playerId,
  teamId,
}: {
  playerId: string
  teamId?: string | null
}) => {
  const { data } = useCurrentPlayerFlightScoresQuery({
    fetchPolicy: 'cache-first',
  })
  const flight = data?.currentPlayerFlightScores ?? []
  // Scramble entries are keyed by team id; everything else by player id.
  const yourEntry = flight.find((e) => e.id === playerId)
  const yourTeamEntry = teamId ? flight.find((e) => e.id === teamId) : undefined
  // Both, merged by hole. A split round splits the card between them — the
  // team owns its one-ball block and the player owns the rest — so reading one
  // entry drew a 12-cell grid with six holes missing and no gap to show it.
  // Every other format fills the whole card from a single entry, and the
  // other is absent.
  const holes = useMemo(() => {
    type Row = NonNullable<(typeof flight)[number]['scores']>[number]
    const merged = new Map<number, Row>()
    for (const entry of [yourTeamEntry, yourEntry]) {
      for (const row of entry?.scores ?? []) merged.set(row.hole.number, row)
    }
    return [...merged.values()].sort((a, b) => a.hole.number - b.hole.number)
  }, [yourEntry, yourTeamEntry])

  if (!holes.length) return null

  return (
    <div className="mx-2 my-3 bg-paper rounded-lg p-4 shadow">
      {/* "per hull" is what separates this card from the breakdown above it,
          which is headed "Tildelte slag" and states the round's total. */}
      <div className="up text-ink-500 mb-3">
        {yourEntry ? 'Tildelte slag per hull' : 'Lagets tildelte slag per hull'}
      </div>
      <div className="grid grid-cols-9 gap-1.5">
        {holes.map((s) => {
          // The row's own allocation — correct for every format, and the
          // only source that survives a split round's per-segment one.
          const n = holeStrokesFor(null, s, s.hole.index)
          const dotColor = n === 0 ? 'bg-ink-200' : 'bg-flag'
          return (
            <div
              key={s.hole.number}
              className={`flex flex-col items-center gap-0.5 py-1.5 rounded ${
                n > 0 ? 'bg-cream-card' : ''
              }`}
            >
              <div className="font-mono num text-[11px] text-ink-700 font-semibold">
                {s.hole.number}
              </div>
              <div className="flex items-center gap-0.5 h-1.5">
                {Array.from({ length: Math.max(1, n) }).map((_, i) => (
                  <span
                    // Decorative dots — index is the stable key.
                    // eslint-disable-next-line react/no-array-index-key
                    key={`dot-${s.hole.number}-${i}`}
                    className={`w-1 h-1 rounded-full ${dotColor}`}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
      <p className="text-[11px] text-ink-500 mt-3">
        Røde prikker = tildelte slag fra hcp. Grå = ingen tildelte slag.
      </p>
    </div>
  )
}

const ChipRules = ({
  chipValues,
}: {
  chipValues: ProfilePlayerFragment['polfEvent']['chipValues']
}) => {
  const rules: { label: string; value: number; suffix?: string }[] = [
    { label: 'Startstack', value: chipValues.startStack, suffix: 'chips' },
    {
      label: 'Stableford-poeng',
      value: chipValues.stablefordPoint,
      suffix: 'chips',
    },
    { label: 'Birdie-bonus', value: chipValues.birdie, suffix: 'chips' },
    { label: 'Eagle-bonus', value: chipValues.eagle, suffix: 'chips' },
    { label: 'HiO-bonus', value: chipValues.holeInOne, suffix: 'chips' },
  ]
  if (chipValues.flightBonusEnabled) {
    rules.push({
      label: 'Flight-bonus',
      value: chipValues.flightBonus,
      suffix: 'chips',
    })
  }
  return (
    <div className="mx-2 my-3 bg-paper rounded-lg shadow overflow-hidden">
      <span className="up text-ink-500 w-full px-4 py-3 flex items-center justify-between text-left">
        Chip-regler
      </span>

      <div className="px-4 pb-4">
        <dl className="flex flex-col gap-2 text-sm">
          {rules.map((r) => (
            <div key={r.label} className="flex items-center justify-between">
              <dt className="text-ink-700">{r.label}</dt>
              <dd className="font-mono num font-bold text-ink-900">
                {formatChips(r.value)} {r.suffix}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

/**
 * Round archive — links to the read-only golf surfaces. Shown ONLY in
 * POKER phase, since the GOLF-phase bottom nav already exposes Scorecard,
 * Topp, and Sidespill as primary tabs. Once poker phase takes over, those
 * tabs vanish from the rail; this card is their new home.
 *
 * Sidegames link is always rendered — the sidegames page handles its own
 * empty state if the event has no closest/longest comps, and we'd need an
 * extra query field to gate it here.
 */
const RoundArchive = ({ playerKey }: { playerKey: string }) => {
  const items: {
    to: string
    label: string
    sub: string
    Icon: typeof IconCard
  }[] = [
    {
      to: `/s/${playerKey}/scorecard`,
      label: 'Scorekort',
      sub: 'Rundens hull-for-hull',
      Icon: IconCard,
    },
    {
      to: `/s/${playerKey}/leaderboard`,
      label: 'Leaderbord fra golf',
      sub: 'Topplisten fra runden',
      Icon: IconLeaderboard,
    },
    {
      to: `/s/${playerKey}/sidegames`,
      label: 'Sidespill',
      sub: 'Closest og longest',
      Icon: IconTarget,
    },
  ]
  return (
    <div className="mx-2 my-3 bg-paper rounded-lg shadow overflow-hidden">
      <div className="px-4 py-2 border-b border-ink-100">
        <span className="up text-ink-500">Min runde</span>
      </div>
      <ul>
        {items.map(({ to, label, sub, Icon }) => (
          <li key={to}>
            <Link
              to={to}
              className="flex items-center gap-3 px-4 py-3 border-b border-ink-100 last:border-b-0 active:opacity-70"
            >
              <span className="w-9 h-9 rounded-full bg-cream-card flex items-center justify-center text-green-800 shrink-0">
                <Icon size={18} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-ink-900">
                  {label}
                </span>
                <span className="block text-[11px] text-ink-500">{sub}</span>
              </span>
              <span aria-hidden className="text-ink-300 text-lg">
                ›
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Profile = () => {
  const { player, phase } = useOutletContext<{
    player: CurrentPlayerCtx
    phase: Phase
  }>()
  const { playerKey = '' } = useParams<{ playerKey: string }>()
  const online = useOnlineStatus()
  const { data, loading } = useCurrentPlayerProfileQuery({
    fetchPolicy: 'cache-and-network',
  })

  if (loading && !data) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster profil…
      </section>
    )
  }
  // Only an empty cache is an error — a failed fetch keeps the last good profile
  // on screen. This page has no poll, so erroring on it stuck until a remount.
  if (!data?.currentPlayer) {
    return online ? (
      <section className="px-4 py-8 text-center text-flag">
        Kunne ikke laste profilen.
      </section>
    ) : (
      <section className="px-4 py-8 text-center text-ink-500">
        Ingen nett. Profilen lastes så snart du er tilkoblet igjen.
      </section>
    )
  }

  const profile = data.currentPlayer
  const { mode } = profile.polfEvent
  // One ball per team: the card, the flight list and the strokes map all
  // belong to the TEAM, and the player's own allowance breakdown would be a
  // number they never play off.
  const isOneBall = isOneBallTeamMode(mode)
  const showTeamCard =
    (isOneBall ||
      mode === PolfEventModeEnum.Bestball ||
      mode === PolfEventModeEnum.Bestworst ||
      mode === PolfEventModeEnum.Segmented) &&
    profile.team != null

  return (
    <section className="pb-4">
      <Hero profile={profile} isOneBall={isOneBall} />
      {showTeamCard && profile.team ? (
        <TeamCard
          team={profile.team}
          mode={mode}
          hcpFactor={profile.polfEvent.hcpFactor}
        />
      ) : null}
      {isOneBall ? null : <AllowanceCard profile={profile} />}
      <FlightCard
        playerId={player.id}
        isOneBall={isOneBall}
        matchPlay={Boolean(player.polfEvent.matchPlay)}
      />
      <StrokesMap
        playerId={player.id}
        teamId={
          isOneBall || mode === PolfEventModeEnum.Segmented
            ? profile.team?.id
            : null
        }
      />
      {usesChips(mode) ? (
        <ChipRules chipValues={profile.polfEvent.chipValues} />
      ) : null}
      {phase === 'POKER' ? <RoundArchive playerKey={playerKey} /> : null}
    </section>
  )
}

export default Profile
